import {useRef, useState, useEffect} from 'react'
import {addHocs} from './shared/helpers/Addhocs'

import Apollo from './Apollo'

import {useMsal, useIsAuthenticated} from '@azure/msal-react'
import {
  CssBaseline,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material'
import CustomBox from './components/common/CustomBox'
import SidebarComponent from './components/SideBar/SideBarComponent'
import IdleTimer from './shared/helpers/IdleTimer'
import TimeoutConfig from './appConfig/sessionTimeout.config.json'
import {Routing} from './AppRouting'
import {useTimeoutContext} from './store/hooks/timeout'
import SessionTimeoutNotificationComponent from './components/Notification/SessionTimeoutNotificationComponent'
import PropTypes from 'prop-types'
import Chatbot from './components/Chatbot'
import {useLocation} from 'react-router-dom'
import {useRolePermissions} from './hooks/useRolesPermissions'
import {useTotalUSPopulation} from './store/hooks/totalUSPopulation'
import {useFilterListDetailQuery, useOutreachPermissions} from './store/hooks/filterListDetail'
import {useInitializeFilters} from './store/hooks/breadCrumbs'

const Root = styled(CustomBox)(() => ({
  display: 'flex',
  flexWrap: 'wrap'
}))

const Content = styled(CustomBox)(() => ({
  flex: '1',
  marginLeft: 64
}))

const Unauthorized = ({handleClose}) => (
  <Dialog open onClose={handleClose} aria-labelledby='dialog-title'>
    <DialogTitle id='dialog-title'>Access Denied</DialogTitle>
    <DialogContent>
      <DialogContentText>You are not authorized to view this resource.</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={handleClose} color='primary'>
        OK
      </Button>
    </DialogActions>
  </Dialog>
)

const App = () => {
  const {instance} = useMsal()
  const [show, setShow] = useState(true)
  const isAuthenticated = useIsAuthenticated()
  const {isSuperAdmin, isDeliveryAdmin, isCustomerSupport, isViewer, userRoles} = useRolePermissions()
  const location = useLocation()

  // Initialize TotalUSPopulation query at app level (was previously in TotalUSPopulationProvider)
  useTotalUSPopulation()

  // Initialize FilterListDetail query at app level - this should only be called once
  useFilterListDetailQuery()

  // Initialize outreach permissions based on user permissions - adds outreach filters to availableFilters
  useOutreachPermissions()

  // Initialize filters from filter list data - this should only be called once
  useInitializeFilters()

  const isDashboard = () => {
    return location.pathname === '/'
  }

  const handleShow = (data) => {
    setShow(data)
  }

  const timerRef = useRef()

  const {isTimeout, setIsTimeout} = useTimeoutContext()

  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false)
  const [isTimeSeriesOpen, setIsTimeSeriesOpen] = useState(false)
  const [isFAQopen, setIsFAQopen] = useState(false)
  const [showChoropleth, setShowChoropleth] = useState(true)

  useEffect(() => {
    timerRef.current = new IdleTimer({
      timeout: TimeoutConfig.APP_SESSION_TIMEOUT_SECONDS, // expire after 10 seconds
      onTimeout: () => {
        setIsTimeout(true)
      }
    })
    if (isTimeout) {
      return () => {
        handleLogout()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogout = () => {
    if (timerRef?.current != null) timerRef.current.cleanUp()
    sessionStorage.clear()
    instance.logout().catch((e) => {
      console.error(e)
    })
  }

  const setTimeOutInactive = () => {
    setIsTimeout(false)
  }

  if (!isAuthenticated) {
    return (
      <Unauthorized
        handleClose={() => {
          handleLogout()
        }}
      />
    )
  }
  return (
    <div className='App'>
      <Root>
        {show && (
          <>
            <CssBaseline />
            <CustomBox display='flex' flex={1} component='main' justifyContent={'center'}>
              <SessionTimeoutNotificationComponent isOpen={isTimeout} setTimeOutInactive={setTimeOutInactive} />
              <SidebarComponent
                isTimeout={isTimeout}
                handleShow={handleShow}
                isAdminPanelOpen={isAdminPanelOpen}
                setIsAdminPanelOpen={setIsAdminPanelOpen}
                setIsTimeSeriesOpen={setIsTimeSeriesOpen}
                isTimeSeriesOpen={isTimeSeriesOpen}
                isFAQopen={isFAQopen}
                setIsFAQopen={setIsFAQopen}
                deliveryAdmin={isDeliveryAdmin()}
                superAdmin={isSuperAdmin()}
                customerSupport={isCustomerSupport()}
                viewer={isViewer()}
              />
              <Content>
                <Routing
                  isTimeout={isTimeout}
                  handleShow={handleShow}
                  isAdminPanelOpen={isAdminPanelOpen}
                  setIsAdminPanelOpen={setIsAdminPanelOpen}
                  setIsTimeSeriesOpen={setIsTimeSeriesOpen}
                  isTimeSeriesOpen={isTimeSeriesOpen}
                  setIsFAQopen={setIsFAQopen}
                  setTimeOutInactive={setTimeOutInactive}
                  deliveryAdmin={isDeliveryAdmin()}
                  roles={userRoles}
                  superAdmin={isSuperAdmin()}
                  customerSupport={isCustomerSupport()}
                  viewer={isViewer()}
                  showChoropleth={showChoropleth}
                  setShowChoropleth={setShowChoropleth}
                />
              </Content>
              {isDashboard() && <Chatbot admin={isDeliveryAdmin()} />}
            </CustomBox>
          </>
        )}
      </Root>
    </div>
  )
}

Unauthorized.propTypes = {
  handleClose: PropTypes.func
}

export default addHocs(App, [Apollo])

/**
 * The main application component for the Health Prism frontend.
 *
 * This component serves as the entry point for the application, managing authentication,
 * user roles, session timeout, and rendering the main layout with various providers and components.
 *
 * Key Features:
 * - Handles user authentication and role-based access control.
 * - Manages session timeout using an idle timer and displays a session timeout notification.
 * - Provides a layout with a sidebar, routing, and main content area.
 * - Integrates multiple context providers to manage application state and data.
 * - Displays an unauthorized access dialog for unauthenticated users.
 * - Includes a chatbot for dashboard interactions.
 *
 * Dependencies:
 * - React, Material-UI, MSAL for authentication, and various custom providers and components.
 *
 * Props:
 * - None directly, but utilizes context and hooks for state management.
 *
 * Returns:
 * - The rendered application layout with conditional rendering based on authentication and roles.
 */
